import React, { useState, useEffect } from 'react'
import {
  Form,
  Button,
  OverlayTrigger,
  Tooltip,
  Alert,
  Spinner,
} from 'react-bootstrap'
import { IconButton } from '@mui/material'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import CustomDataGrid from '../components/CustomDataGrid'
import { createUser, fetchUsersFromDB, sendEmail, updateUserStatus } from '../utils/api'
import { validateForm, formatPhoneNumber } from '../utils/PreviewUtils'
import {
  existingUsersGridColumns,
  existingUsersRowMapping,
  generateGridRows,
  generateGridColumns,
  usersGridOptionalColumns,
} from '../utils/PageUtils'
import PageHelmet from '../components/PageHelmet'
import '../styles/Users.css'
import PropTypes from 'prop-types'

function Users({ userRole, bearerToken }) {
  const [statusMessage, setStatusMessage] = useState(null)
  const [isError, setIsError] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [existingUsers, setExistingUsers] = useState([])
  const [usersLength, setUsersLength] = useState(0)
  const [usersLoading, setUsersLoading] = useState(false)
  const [refreshGrid, setRefreshGrid] = useState(true)
  const [selectedRowId, setSelectedRowId] = useState(null)
  const [selectedUser, setSelectedUser] = useState(null)
  const [emailError, setEmailError] = useState(null)
  const [emailSuccess, setEmailSuccess] = useState(null)
  const [formValues, setFormValues] = useState({
    name: '',
    user_name: '',
    email: '',
    phone: '',
    countryCode: '+1',
  })
  const newUserFields = [
    {
      name: 'name',
      label: ' Organizational Name ',
      tooltip: 'Name of the Organization/business.',
      placeholder: 'For ex: Apple inc.',
    },
    {
      name: 'user_name',
      label: 'Name',
      tooltip: 'Name of the User.',
      placeholder: 'For ex: John Doe',
    },
    {
      name: 'email',
      label: 'Email Address',
      tooltip: 'Valid email of User for receiving credentials.',
      placeholder: 'For ex: name@example.com',
    },
  ]


  useEffect(() => {
    const fetchUserCredentials = async () => {
      setUsersLoading(true)
      setError(null)
      if (!bearerToken) {
        console.warn('No access token available')
        return
      }
      try {
        const response = await fetchUsersFromDB(bearerToken)

        if (response.status === 200) {
          const data = response.data
          if (data.count > 0) {
            setUsersLength(data.count)
            console.log('data.users', data.users)

            console.log('users after format', data.users)
            setExistingUsers(data.users)
          } else {
            return []
          }
        } else if (response.status === 404) {
          setError('No user credentials were returned by the API')
          return []
        } else if (response.status === 500) {
          return []
        }
      } catch {
        return []
      } finally {
        setUsersLoading(false)
        if (refreshGrid) {
          setRefreshGrid(false)
        }
      }
    }
    if (refreshGrid) {
      fetchUserCredentials()
    }
  }, [refreshGrid])

  const handleInputChange = (e) => {
    const name = e.target.name
    const value = e.target.value


    setFormValues((prevValues) => {
      let newValues;
      if (name === 'phone') {
        const formattedPhone = formatPhoneNumber(value)
        newValues = {
          ...prevValues,
          phone: formattedPhone,
        }
      } else {
        newValues = {
          ...prevValues,
          [name]: value,
        }
      }
      console.log('State Update:', { key: name, val: value, newState: newValues })
      return newValues
    })
  }
  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setStatusMessage(null)
    setIsError(false)
    setEmailError(null)      // Clear email error message
    setEmailSuccess(null)    // Clear email success message

    console.log('form values', formValues)

    try {
      const { name, user_name, email, phone, countryCode } = formValues;
      console.log('type of name', typeof name)
      console.log('type of email', typeof email)

      const trimmedName = name.trim()
      const trimmedEmail = email.trim()
      const trimmedUserName = user_name.trim()

      const fullPhone = `${countryCode}${phone.replace(/\D/g, '')}`
      console.log('full phone', fullPhone)

      const { isValid, errorMessage } = validateForm(
        trimmedName,
        fullPhone,
        trimmedEmail,
        trimmedUserName
      )

      if (!isValid) {
        setStatusMessage(errorMessage)
        setIsError(true)
        setLoading(false)
        return;
      }
      const action = "CREATE_USER";
      const response = await createUser(
        trimmedName,
        trimmedUserName,
        trimmedEmail,
        fullPhone,
        bearerToken,
        action
      )
      setStatusMessage(response.message)
      if (response.success) {
        setRefreshGrid(true)
        setFormValues({
          name: '',
          email: '',
          phone: '',
          countryCode: '+1',
          user_name: '',
        })
      }
      setIsError(!response.success)
    } catch {
      setStatusMessage('An error occurred while creating the user.')
      setIsError(true)
    } finally {
      setLoading(false)

    }
  }

  const handleRowClick = (e) => {

    setEmailError(null)
    setEmailSuccess(null)
    setStatusMessage(null)
    console.log('row clicked', e)
    console.log('selected row', e)
    if (selectedRowId === e.id) {
      setSelectedRowId(null)
      setSelectedUser(null)
    } else {
      setSelectedRowId(e.id)
      setSelectedUser(existingUsers.find(user => user.email === e.row.email))
    }

  }


  const handleResendEmail = async (selectedUser) => {
    console.log('Resend email clicked for user:', selectedUser)
    setEmailError(null)
    setEmailSuccess(null)
    if (!selectedUser) {
      console.warn('No user selected for resending email')
      setEmailError('Please select a user to resend the email.')
      setEmailSuccess(null)
      return;
    }
    const { email, user_id, confirmation_status } = selectedUser;
    console.log('Resend email to user:', selectedUser)
    try {
      console.log('email:', email);
      console.log('sub:', user_id);
      if (!email || !user_id || !confirmation_status) {
        setEmailError('Selected user does not have valid attributes to complete this request.')
        return;
      }
      if (confirmation_status === 'yes') {
        setEmailError(`The selected user ${email} is already confirmed. No need to resend the email.`)
        return;
      }
      const action = "RESEND_ONBOARDING_EMAIL";
      const status = await sendEmail(bearerToken, email, user_id, action);
      console.log('Email resend status:', status);
      if (status.success) {
        setEmailSuccess(status.message)
        setEmailError(null)
        setSelectedRowId(null)
        setSelectedUser(null)
      } else {
        setEmailError(status.message)
      }
    } catch (error) {
      setEmailError('An error occurred while resending the email.')
      return;
    }
  }

  const handleUserEnable = async (selectedUser) => {
    console.log('Toggle user status clicked for user:', selectedUser)
    setEmailError(null)
    setEmailSuccess(null)
    if (!selectedUser) {
      console.warn('No user selected for toggling status')
      setEmailError('Please select a user to enable.')
      setEmailSuccess(null)
      return;
    }
    const confirmed = window.confirm('Are you sure you want to enable this user?');
    if (!confirmed) {
      return;
    }
    const { user_id, account_status, email } = selectedUser;
    try {
      if (!user_id || !account_status) {
        setEmailError('Selected user does not have valid user ID or account status.')
        return;
      }
      if (account_status === 'active') {
        setEmailError(`The selected user ${email} is already enabled.`)
        return;
      }
      const response = await updateUserStatus(bearerToken, user_id, account_status, 'enable');
      console.log('User status update response:', response);
      if (response.success) {
        setEmailSuccess(`${response.message} for user ${email}`)
        setEmailError(null)
        setRefreshGrid(true)
        setSelectedRowId(null)
        setSelectedUser(null)
      } else {
        setEmailError(response.message)
      }
    } catch (error) {
      setEmailError('An error occurred while updating user status.')
      return;
    }
  }

  const handleUserDisable = async (selectedUser) => {
    console.log('Toggle user status clicked for user:', selectedUser)
    setEmailError(null)
    setEmailSuccess(null)
    if (!selectedUser) {
      console.warn('No user selected for toggling status')
      setEmailError('Please select a user to disable.')
      setEmailSuccess(null)
      return;
    }
    const confirmed = window.confirm('Are you sure you want to disable this user?');
    if (!confirmed) {
      return;
    }
    const { user_id, account_status, email } = selectedUser;
    try {
      if (!user_id || !account_status) {
        setEmailError('Selected user does not have valid user ID or account status.')
        return;
      }
      if (account_status === 'disabled') {
        setEmailError(`The selected user ${email} is already disabled.`)
        return;
      }
      const response = await updateUserStatus(bearerToken, user_id, account_status, 'disable');
      console.log('User status update response:', response);
      if (response.success) {
        setEmailSuccess(`${response.message} for user ${email}`)
        setEmailError(null)
        setRefreshGrid(true)
        setSelectedRowId(null)
        setSelectedUser(null)
      } else {
        setEmailError(response.message)
      }
    } catch (error) {
      setEmailError('An error occurred while updating user status.')
      return;
    }
  }

  const existingUsersRows = generateGridRows(
    existingUsers,
    existingUsersRowMapping,
  )
  const existingUsersColumns = generateGridColumns(
    existingUsersGridColumns,
    "name",
    undefined,
    usersGridOptionalColumns
  );
  return userRole === 'admin' ? (
    <div>
      <PageHelmet
        title="Users"
        description="Create and view user."
        url={`${process.env.REACT_APP_COGNITO_REDIRECT_URL}/users`}
      />
      <div className="user-container">
        <h2 className="create-user-heading">Create User</h2>
        <Form className="proof-form-container" onSubmit={handleSubmit}>
          {newUserFields.map((field) => (
            <Form.Group
              controlId={`form-${field.name}`}
              className="form-group-custom"
              key={field.name}
            >
              <div className="modal-form-field" key={field.name}>
                <Form.Label className="custom-label">
                  {field.label}
                  <span style={{ color: 'red', marginLeft: 3 }}>*</span>
                </Form.Label>
                <OverlayTrigger
                  placement="top"
                  overlay={<Tooltip>{field.tooltip}</Tooltip>}
                >
                  <IconButton>
                    <HelpOutlineIcon sx={{ fontSize: 15 }} />
                  </IconButton>
                </OverlayTrigger>
              </div>
              <Form.Control
                type="string"
                name={field.name}
                placeholder={field.placeholder}
                onChange={handleInputChange}
                value={formValues[field.name]}
                className="custom-placeholder"
              />
            </Form.Group>
          ))}

          <Form.Group
            className="form-group-custom"
            controlId="form-countryCode-phone"
          >
            <div className="modal-form-field">
              <Form.Label className="custom-label">
                Mobile Phone
                <span style={{ color: 'red', marginLeft: 3 }}>*</span>
              </Form.Label>
              <OverlayTrigger
                placement="top"
                overlay={
                  <Tooltip>
                    Valid phone number of User capable of receiving Text/SMS.
                  </Tooltip>
                }
              >
                <IconButton>
                  <HelpOutlineIcon sx={{ fontSize: 15 }} />
                </IconButton>
              </OverlayTrigger>
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <Form.Control
                type="text"
                name="countryCode"
                placeholder="+1"
                value={formValues.countryCode}
                onChange={handleInputChange}
                style={{ maxWidth: '80px' }}
                className="custom-placeholder"
              />
              <Form.Control
                type="text"
                name="phone"
                placeholder="123-456-7890"
                value={formValues.phone}
                onChange={handleInputChange}
                className="custom-placeholder"
              />
            </div>
          </Form.Group>

          {statusMessage && (
            <div className="message-container">
              <Alert variant={isError ? 'danger' : 'success'}>
                {statusMessage}
              </Alert>
            </div>
          )}
          <div style={{ textAlign: 'center' }}>
            <Button variant="dark" type="submit" className="create-button">
              {loading ? 'Creating...' : 'Create User'}
            </Button>
          </div>
        </Form>

      </div>

      {usersLoading ? (
        <div className="loading-container">
          <Spinner animation="border" role="status" />
        </div>
      ) : (
        <div className="existing-grid">
          <h1 className="user-heading">Existing Users</h1>
          {emailError && (
            <div className="message-container">
              <Alert variant='danger'>{emailError}</Alert>
            </div>
          )}
          {emailSuccess && (
            <div className="message-container">
              <Alert variant='success'>{emailSuccess}</Alert>
            </div>
          )}
          {error ? (
            <div className="message-container">
              <Alert variant="danger">{error}</Alert>
            </div>
          ) : (
            <CustomDataGrid
              rows={existingUsersRows}
              columns={existingUsersColumns}
              totalRows={usersLength}
              onRowClick={handleRowClick}
              selectedRowId={selectedRowId}
              toolbarProps={{
                showResendEmail: true,
                onResendEmail: handleResendEmail,
                selectedRow: selectedUser,
                showUserActions: true,
                onEnableUser: handleUserEnable,
                onDisableUser: handleUserDisable,
              }}
            />
          )}
        </div>
      )}

    </div>
  ) : (
    <div className="user-error">
      <h1>404 - Page Not Found</h1>
      <br />
      <p>The page you're looking for doesn't exist .</p>
    </div>
  )
}

Users.propTypes = {
  userRole: PropTypes.string.isRequired,
  bearerToken: PropTypes.string.isRequired,
}

export default Users
